import {useCallback, useEffect, useRef} from 'react';
import {useInView} from 'react-intersection-observer';
import {omitNullUndefined} from '../../../../common/omitNullUndefined';
import {requestContext} from '../../../../common/requestContext';
import {useIsResponsiveFoldable} from '../../../../common/useIsResponsiveFoldable.hook';
import useTypedDispatch from '../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../common/useTypedSelector';
import {setIsIpxMediaMuted} from '../../../../config/configSlice';
import {useNetworkUsingAgreement} from '../../../../config/useNetworkUsingAgreement.hook';
import {BannerItem} from '../../../../design-system/organisms/BannerItem/BannerItem';
import {
  BannerItem as BannerItemType,
  GroupType,
} from '../../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {getBannerStartAutoplayEventKey} from '../../../../main/getBannerStartAutoplayEventKey.function';
import {getBannerStopAutoplayEventKey} from '../../../../main/getBannerStopAutoplayEventKey.function';
import {CommonItemProps} from '../../type';

export function Banner({item, parentGroup}: CommonItemProps) {
  const isFoldable = useIsResponsiveFoldable();
  const {isSelected, parentId, parentGroupType, hasIndicator} =
    useTypedSelector(state => {
      const parentId = parentGroup.id;
      const selectedBannerId =
        state.pagewebLayout.bannerInfo.selectedBanner[parentId];
      const hasIndicator =
        state.pagewebLayout.bannerInfo.hasIndicator[parentId];
      return {
        isSelected: selectedBannerId === item.id,
        parentId,
        parentGroupType: state.pagewebLayout.entities.groups[parentId].type,
        hasIndicator,
      };
    });
  const {
    bannerType,
    thumbnail,
    badgeList,
    statusBadge,
    title,
    altText,
    videoUrl,
    caption,
    metaList,
    bannerViewType,
    scheme,
    titleImage,
    seriesId,
    eventLog,
    discountRateText,
    characterImage,
    backgroundColor,
  } = item as BannerItemType;

  const startBannerAutoplay = useCallback(() => {
    window.dispatchEvent(new Event(getBannerStartAutoplayEventKey(parentId)));
  }, [parentId]);

  const stopBannerAutoplay = useCallback(() => {
    window.dispatchEvent(new Event(getBannerStopAutoplayEventKey(parentId)));
  }, [parentId]);

  const onVideoStart = useCallback(() => {
    stopBannerAutoplay();
    aggregateAndSendLog({
      targetElem: bannerVideoRef.current as HTMLVideoElement,
      actionType: 'Event',
      actionName: '영상재생_자동',
      click: {
        imp_provider: 'service',
      },
      customProps: {
        user_action: 'play',
        content_cnt: null,
        section_cnt: null,
      },
      eventMeta: undefined,
    });
  }, [stopBannerAutoplay]);

  const onVideoEnded = useCallback(() => {
    startBannerAutoplay();
  }, [startBannerAutoplay]);

  const isVideoMuted = useTypedSelector(state => state.config.isIpxMediaMuted);
  const dispatch = useTypedDispatch();
  const onVideoMutedChange = useCallback(
    (isMuted: boolean) => {
      aggregateAndSendLog({
        targetElem: bannerVideoRef.current as HTMLVideoElement,
        actionType: 'Event',
        actionName: '사운드_클릭',
        click: {
          imp_provider: 'service',
          setnum: null,
          ordnum: null,
          copy: isMuted ? 'off' : 'on',
        },
        customProps: {
          content_cnt: null,
          section_cnt: null,
          user_action: 'click',
        },
        eventMeta: undefined,
      });
      dispatch(setIsIpxMediaMuted(isMuted));
    },
    [dispatch]
  );

  const bannerVideoRef = useRef<HTMLVideoElement>(null);
  const {isMobile} = requestContext.requestInfo.display;

  const playBannerVideo = useCallback(() => {
    bannerVideoRef.current?.play();
  }, []);

  const {isNetworkUsingAgreed, showAgreementDialog, DialogComponent} =
    useNetworkUsingAgreement({
      isMobile,
      onClickOk: playBannerVideo,
      onClose: startBannerAutoplay,
    });

  const onClickPlayVideo = useCallback(() => {
    if (!isNetworkUsingAgreed) {
      showAgreementDialog();
      stopBannerAutoplay();
    } else {
      playBannerVideo();
    }
  }, [
    isNetworkUsingAgreed,
    showAgreementDialog,
    stopBannerAutoplay,
    playBannerVideo,
  ]);

  const {ref, inView, entry} = useInView({
    threshold: 0.5,
  });

  const viewImpTimer = useRef(0);

  useEffect(() => {
    // Top 섹션 빅배너 아이템에 대해서만 ViewImp를 남겨야 합니다.
    if (
      parentGroupType === GroupType.BigBanner &&
      inView &&
      isSelected &&
      entry?.target
    ) {
      viewImpTimer.current = window.setTimeout(() => {
        aggregateAndSendLog({
          targetElem: entry.target as HTMLElement,
          actionType: 'ViewImp',
          actionName: '빅배너_노출',
          customProps: {
            ...(omitNullUndefined(eventLog?.customProps) || {}),
            user_action: 'imp',
          },
          ...(eventLog?.viewimp_contents && {
            viewimp_contents:
              omitNullUndefined(eventLog.viewimp_contents) || {},
          }),
        });
      }, 1000);
    }
    return () => window.clearTimeout(viewImpTimer.current);
  }, [entry?.target, eventLog, inView, isSelected, parentGroupType]);

  return (
    <div ref={ref} className="overflow-hidden rounded-16pxr">
      <BannerItem
        bannerUid={eventLog?.customProps?.banner_uid}
        bannerType={bannerType}
        bannerImageSrc={thumbnail}
        bannerVideoSrc={videoUrl}
        viewType={bannerViewType}
        titleText={title}
        altText={altText}
        caption={caption}
        badgeList={badgeList}
        statusBadge={statusBadge}
        metaList={metaList}
        isSelected={
          parentGroupType === GroupType.EventBanner ||
          (parentGroupType === GroupType.ResponsiveBanner && isFoldable) ||
          isSelected
        }
        isParentCarouselHasIndicator={hasIndicator}
        scheme={scheme}
        onVideoStart={onVideoStart}
        onVideoEnded={onVideoEnded}
        isMuted={isVideoMuted}
        onVideoMutedChange={onVideoMutedChange}
        isMobile={isMobile}
        isFoldable={isFoldable}
        ref={bannerVideoRef}
        onClickPlayVideo={onClickPlayVideo}
        titleImageSrc={titleImage}
        onClickCapture={e =>
          LOG_CLICK_EVENT(`${seriesId ? '작품' : '빅배너'}_클릭`, e)
        }
        {...putTiaraObjectProps(eventLog)}
        discountRateText={discountRateText}
        characterImageSrc={characterImage}
        backgroundColor={backgroundColor}
      />
      {DialogComponent}
    </div>
  );
}
